<template>
  <div class="container-fluid">
    <div class="h3">Rejestracja nowego użytkownika</div>
    <ValidationObserver
      ref="observer"
      tag="form"
      class="form"
      @submit.prevent="registerRequest()"
    >
      <div class="form__fields">
        <validation-provider
          rules="required"
          v-slot="{ errors }"
          class="form__group form__group--inline"
        >
          <label class="form__label form__label--inline form__label--required"
            >Imie</label
          >
          <div
            class="form__group"
            :class="{ 'form__group--error': errors.length > 0 }"
          >
            <input
              class="form__input"
              v-model.trim="name"
              autocomplete="off"
              placeholder="Imię"
              name="name"
            />
            <FormError :errors="errors" />
          </div>
        </validation-provider>
        <validation-provider
          rules="required"
          v-slot="{ errors }"
          class="form__group form__group--inline"
        >
          <label class="form__label form__label--inline form__label--required"
            >Nazwisko</label
          >
          <div
            class="form__group"
            :class="{ 'form__group--error': errors.length > 0 }"
          >
            <input
              class="form__input form__input--inline"
              v-model.trim="lastName"
              name="lastName"
              autocomplete="off"
              placeholder="Nazwisko"
            />
            <FormError :errors="errors" />
          </div>
        </validation-provider>
        <validation-provider
          rules="required"
          v-slot="{ errors }"
          class="form__group form__group--inline"
        >
          <label class="form__label form__label--inline form__label--required"
            >Telefon</label
          >
          <div
            class="form__group"
            :class="{ 'form__group--error': errors.length > 0 }"
          >
            <input
              class="form__input"
              v-model.trim="phone"
              autocomplete="off"
              placeholder="Telefon"
              name="phone"
            />
            <FormError :errors="errors" />
          </div>
        </validation-provider>
        <validation-provider
          rules="required|email"
          v-slot="{ errors }"
          class="form__group form__group--inline"
        >
          <label class="form__label form__label--inline form__label--required"
            >Adres e-mail</label
          >
          <div
            class="form__group"
            :class="{ 'form__group--error': errors.length > 0 }"
          >
            <input
              class="form__input"
              v-model.trim="email"
              autocomplete="off"
              name="email"
              placeholder="E-mail"
            />
            <FormError :errors="errors" />
          </div>
        </validation-provider>
        <validation-provider
          rules="required|nip"
          v-slot="{ errors }"
          class="form__group form__group--inline"
        >
          <label class="form__label form__label--inline">NIP</label>
          <div
            class="form__group"
            :class="{ 'form__group--error': errors.length > 0 }"
          >
            <input
              class="form__input"
              v-model.trim="nip"
              autocomplete="off"
              name="nip"
              placeholder="Numer identyfikacji podatkowej"
            />
            <FormError :errors="errors" />
          </div>
        </validation-provider>
        <validation-provider
          rules="required|min_length:8|password:@confirm"
          v-slot="{ errors }"
          class="form__group form__group--inline"
        >
          <label class="form__label form__label--inline form__label--required"
            >Hasło</label
          >
          <div
            class="form__group"
            :class="{ 'form__group--error': errors.length > 0 }"
          >
            <input
              class="form__input"
              type="password"
              autocomplete="off"
              v-model.trim="password"
              placeholder="min. 8 znaków"
            />
            <FormError :errors="errors" />
          </div>
        </validation-provider>
        <validation-provider
          name="confirm"
          rules="required"
          v-slot="{ errors }"
          class="form__group form__group--inline"
        >
          <label class="form__label form__label--inline form__label--required"
            >Powtórz hasło</label
          >
          <div
            class="form__group"
            :class="{ 'form__group--error': errors.length > 0 }"
          >
            <input
              class="form__input form__input--inline"
              type="password"
              v-model.trim="repeatPassword"
              autocomplete="off"
              placeholder="Powtórz hasło"
            />
            <FormError :errors="errors" />
          </div>
        </validation-provider>
        <validation-provider
          :rules="{ required: { allowFalse: false } }"
          v-slot="{ errors }"
          class="form__group form__group--inline"
        >
          <label class="form__label form__label--inline"></label>
          <div class="form__group">
            <input type="checkbox" id="acceptRules" v-model="acceptRules" />
            <label for="acceptRules">
              <span class="form__label"
                >Wyrażam zgodę na przetwarzanie podanych przeze mnie danych
                osobowych i akceptuję Regulamin i Politykę prywatności.</span
              >
            </label>
            <FormError :errors="errors" />
          </div>
        </validation-provider>
        <ButtonPrimary class="form__button">Załóż konto</ButtonPrimary>
      </div>
    </ValidationObserver>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/scss/components/entry-form";
</style>

<style lang="scss">
@import "@/assets/scss/components/form/form";
</style>

<script>
import { mapActions, mapGetters } from "vuex";
import { actionTypes } from "@/store/modules/User/action-types";
import { getterTypes } from "@/store/modules/User/getter-types";
import { ValidationObserver, ValidationProvider, extend } from "vee-validate";
import { required, email } from "vee-validate/dist/rules";
import { requiredMsg, emailMsg } from "@/validations";
import { validateMessages } from "@/config";

extend("required", {
  ...required,
  message: requiredMsg,
});

extend("email", {
  ...email,
  message: emailMsg,
});

extend("password", {
  params: ["target"],
  validate(value, { target }) {
    return value === target;
  },
  message: "Hasła różnią się od siebie",
});

extend("min_length", {
  validate(value, args) {
    if (value.length >= args.length) {
      return true;
    }
    return `Minimalna liczba znaków to ${args.length}`;
  },
  params: ["length"],
});

extend("nip", {
  validate(value) {
    if (typeof value !== "string") return "Nieprawidłowy numer NIP";

    let weight = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    let sum = 0;
    let controlNumber = parseInt(value.substring(9, 10));
    let weightCount = weight.length;
    for (let i = 0; i < weightCount; i++) {
      sum += parseInt(value.substr(i, 1)) * weight[i];
    }

    if (sum % 11 === controlNumber) {
      return true;
    }
    return "Nieprawidłowy numer NIP";
  },
});

export default {
  name: "RegisterForm",
  components: {
    ButtonPrimary: () => import("@/pages/components/buttons/ButtonPrimary"),
    FormError: () => import("@/pages/components/FormError"),
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      name: "",
      lastName: "",
      phone: "",
      email: "",
      nip: "",
      password: "",
      repeatPassword: "",
      acceptRules: "",
    };
  },
  methods: {
    ...mapActions("UserModule", [actionTypes.ADD_REGISTER_USER]),
    ...mapGetters("UserModule", [getterTypes.GET_REGISTER_USER]),
    async registerRequest() {
      const isValid = await this.$refs.observer.validate();
      if (!isValid) {
        return;
      }

      const data = {
        email: this.email,
        plainPassword: this.password,
        firstName: this.name,
        lastName: this.lastName,
        channel: "MAIN_CHANNEL",
        vatId: this.nip,
      };

      this.ADD_REGISTER_USER(data)
        .then(() => {
          this.$notify({
            group: validateMessages.group,
            type: "success",
            text:
              "Konto zostało utworzone.<br>Zostaniesz poinformowany mailowo o aktywowaniu konta.",
            duration: 100000,
          });

          this.name = this.lastName = this.phone = this.email = this.nip = this.password = this.repeatPassword = this.acceptRules = '';

          this.$nextTick(() => {
            this.$refs.observer.reset();
          });
        })
        .catch(() => {
          this.$notify({
            group: validateMessages.group,
            type: "error",
            text: "Wystąpił błąd podczas tworzenia konta.",
            duration: 30000,
          });
        });
    },
  },
};
</script>